import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { DateField } from '@/components/ui/date-time-field';
import { Label } from '@/components/ui/label';
import { EmployeePicker } from '@/components/registration/EmployeePicker';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi } from './mfgApi';
import { todayJst } from '@/utils/jstDate';

const today = () => todayJst();

export default function CompleteScheduleDialog({ open, schedule, onClose, onDone }) {
  const { t } = useT();
  const [performedOn, setPerformedOn] = useState(today());
  const [performedBy, setPerformedBy] = useState('');
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState('');
  const [employees, setEmployees] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) return;
    setPerformedOn(today()); setPerformedBy(''); setChecked({}); setNotes('');
    mfgApi.employees()
      .then((list) => {
        const rows = Array.isArray(list) ? list : (list?.items || []);
        setEmployees(rows.filter((e) => (e.status || 'active') === 'active')
          .map((e) => ({ id: e.id, name: e.name || e.code || e.email || e.id })));
      })
      .catch(() => setEmployees([]));
  }, [open]);

  const save = async () => {
    if (!schedule) return;
    setBusy(true);
    try {
      const results = (schedule.checklist || []).filter((_, i) => checked[i]);
      await mfgApi.completeSchedule(schedule.id, {
        performed_on: performedOn, performed_by: performedBy || null,
        checklist_results: results, notes: notes || null,
      });
      toast.success(t('mfg.common.saved')); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || t('mfg.common.error')); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="complete-schedule-dialog">
        <DialogHeader><DialogTitle>{t('mfg.maint.completeTitle')}{schedule ? ` — ${schedule.title}` : ''}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.performedOn')}</Label><DateField testId="complete-date-input" value={performedOn} onChange={(v) => setPerformedOn(v)} className="mt-1" inputClassName="h-10" /></div>
            <div>
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.performedBy')}</Label>
              {/* 2026-10-22 · 実施者も社員名簿から選ぶ（古い手入力はそのまま出す） */}
              <EmployeePicker value={performedBy} employees={employees}
                onChange={setPerformedBy} testId="complete-performed-by" />
            </div>
          </div>
          {(schedule?.checklist || []).length > 0 && (
            <div className="space-y-1.5">
              <Label className="text-xs font-semibold text-zinc-600">{t('mfg.maint.checklist')}</Label>
              {schedule.checklist.map((c, i) => (
                <label key={`${i}-${c}`} className="flex items-center gap-2 text-sm text-zinc-700">
                  <Checkbox checked={!!checked[i]} onCheckedChange={(v) => setChecked((p) => ({ ...p, [i]: v }))} data-testid={`complete-check-${i}`} /> {c}
                </label>
              ))}
            </div>
          )}
          <div><Label className="text-xs font-semibold text-zinc-600">{t('mfg.common.notes')}</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} className="h-10 mt-1" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{t('mfg.common.cancel')}</Button>
          <Button data-testid="complete-confirm-btn" onClick={save} disabled={busy} className="bg-teal-700 hover:bg-teal-800">{t('mfg.maint.complete')}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
